import { useState } from 'react';

import { MatterType } from '../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddUserToMatters } from '@/api/mutations/useAddUserToMatters';
import { useRemoveUserFromMatters } from '@/api/mutations/useRemoveUserFromMatters';
import { useGetMatters } from '@/api/queries/useGetMatters';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { UsersTable } from '@/new/components/users-table';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';

export function ManageUsers() {
  const { user } = useUserContext();
  if (!user) return null;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [addMatterModalOpen, setAddMatterModalOpen] = useState(false);
  const [matterSearchQuery, setMatterSearchQuery] = useState('');
  const [selectedMatters, setSelectedMatters] = useState<Set<string>>(new Set());

  const { data: matters } = useGetMatters();
  const { mutate: addUserToMatters } = useAddUserToMatters();
  const { mutate: removeUserFromMatters } = useRemoveUserFromMatters();

  const getAvailableMatters = () => {
    if (!selectedUser || !matters) return [];
    // Get IDs of matters user already has access to and filter them out
    const userMatterIds = new Set(selectedUser.matters?.map((matter) => matter.id) || []);
    return matters.filter((matter) => !userMatterIds.has(matter.id));
  };

  const filteredMatters = getAvailableMatters().filter(
    (matter) =>
      !matter.isDraft &&
      (matter.name.toLowerCase().includes(matterSearchQuery.toLowerCase()) ||
        (matter.code && matter.code.toLowerCase().includes(matterSearchQuery.toLowerCase())) ||
        (matter.description && matter.description.toLowerCase().includes(matterSearchQuery.toLowerCase()))),
  );

  // Handle matter selection
  const toggleMatterSelection = (matterId: string) => {
    const newSelectedMatters = new Set(selectedMatters);
    if (newSelectedMatters.has(matterId)) {
      newSelectedMatters.delete(matterId);
    } else {
      newSelectedMatters.add(matterId);
    }
    setSelectedMatters(newSelectedMatters);
  };

  // Handle adding selected matters to user
  const handleAddUserToMatters = () => {
    if (!selectedUser) return;

    addUserToMatters({ userId: selectedUser.id, matterIds: Array.from(selectedMatters) });

    trackCustomEvent(TrackingEventsV2.AddUserToMatter);

    // Close the modal and reset selection
    setAddMatterModalOpen(false);
    setSelectedMatters(new Set());
    setMatterSearchQuery('');
  };

  const handleRemoveUserFromMatter = ({ userId, matterIds }: { userId: string; matterIds: string[] }) => {
    if (!selectedUser) return;

    removeUserFromMatters({ userId, matterIds });
    // Remove the matter from the selected user
    const updatedMatters = selectedUser.matters?.filter((matter) => !matterIds.includes(matter.id));

    trackCustomEvent(TrackingEventsV2.RemoveUserFromMatter);

    setSelectedUser({ ...selectedUser, matters: updatedMatters });
  };

  // Handler for opening the user matters modal
  const handleOpenUserMattersModal = (user: User) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/manage-users', label: 'Manage Users', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Manage Users" />
        </PageHeader>
      </PageContainer>

      <UsersTable organisationId={user.organisationId} onViewUserMatters={handleOpenUserMattersModal} />

      {/* Current User Matters Modal */}
      <Dialog open={modalOpen} onOpenChange={(v) => setModalOpen(v)}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Matters for {selectedUser?.email}</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-between border-b pb-4">
            <p>
              Belongs to <strong>{selectedUser?.matters.length}</strong>{' '}
              {selectedUser?.matters.length === 1 ? 'matter' : 'matters'}
            </p>

            <Dialog open={addMatterModalOpen} onOpenChange={setAddMatterModalOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="primary">
                  <Icon className="lucide--folder-plus" />
                  <span>Add user to other Matters</span>
                </Button>
              </DialogTrigger>

              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add {selectedUser?.email} to Matters</DialogTitle>
                </DialogHeader>

                <SearchInput
                  placeholder="Search matters..."
                  value={matterSearchQuery}
                  onChange={(e) => setMatterSearchQuery(e.target.value)}
                />

                <div className="mt-2 max-h-64 overflow-auto">
                  {filteredMatters.map((matter) => {
                    const isSelected = selectedMatters.has(matter.id);
                    return (
                      <div
                        key={matter.id}
                        className={`flex cursor-pointer items-center gap-3 px-2 py-3 transition-colors not-last:border-b`}
                        onClick={() => toggleMatterSelection(matter.id)}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <p className="font-medium">
                            {matter.name} {matter.code && <kbd className="ml-1">{matter.code}</kbd>}
                          </p>
                          {matter.description && <p className="line-clamp-1 text-gray-500">{matter.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button>Cancel</Button>
                  </DialogClose>
                  <Button variant={'primary'} disabled={selectedMatters.size === 0} onClick={handleAddUserToMatters}>
                    Add user to {selectedMatters.size} {selectedMatters.size === 1 ? 'matter' : 'matters'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="max-h-64 overflow-auto">
            {selectedUser?.matters
              .filter((matter) => matter.type !== MatterType.Personal)
              .map((matter) => (
                <div className="flex items-center justify-between gap-4 py-3 not-last:border-b">
                  <div className="flex flex-col gap-1">
                    <p className="font-medium">
                      {matter.name} {matter.code && <kbd className="ml-1">{matter.code}</kbd>}
                    </p>
                    {matter.description && <p className="line-clamp-1 text-gray-500">{matter.description}</p>}
                  </div>

                  {matter.createdById === selectedUser?.id ? (
                    <Chip className={CHIP_COLOURS.default}>Matter Creator</Chip>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveUserFromMatter({ userId: selectedUser.id, matterIds: [matter.id] });
                      }}
                    >
                      Remove Access
                    </Button>
                  )}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
